import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaTrophy, FaUsers, FaClock, FaPlay, FaSpinner, FaPlus, FaFire, FaStar, FaCrown, FaGem, FaBolt, FaShieldAlt, FaRocket, FaAtom, FaLock } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { 
  getActiveChallenges, 
  createChallengeSession, 
  getUserChallengeSession,
  getChallengeParticipants,
  joinChallenge,
  type ChallengeSession 
} from '../lib/challenges';
import toast from 'react-hot-toast';

interface Song {
  id: string;
  creator: string;
  song_cover: string;
  language: string;
  is_free: boolean;
}

export default function ChallengeMode() {
  const [challenges, setChallenges] = useState<ChallengeSession[]>([]);
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSong, setSelectedSong] = useState<string>('');
  const [userCurrentChallenge, setUserCurrentChallenge] = useState<any>(null);
  const [userParticipatedChallenges, setUserParticipatedChallenges] = useState<Set<string>>(new Set());
  const [hasAccess, setHasAccess] = useState(false);
  const navigate = useNavigate();
  const user = getCurrentUser();

  useEffect(() => {
    loadChallenges();
    loadSongs();
    checkAccess();
    checkUserCurrentChallenge();
    loadUserParticipatedChallenges();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadChallenges();
      loadUserParticipatedChallenges();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const checkAccess = async () => {
    if (!user) return;

    try {
      if (user.is_admin) {
        setHasAccess(true);
        return;
      }

      // Check for valid coupon usage
      const { data: couponUsage, error: couponError } = await supabase
        .from('stripecouponusage')
        .select('*')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString());

      if (couponError && couponError.code !== 'PGRST116') {
        throw couponError;
      }

      // Check for practice subscription
      const { data: practiceSubscription, error: subscriptionError } = await supabase
        .from('stripepracticesubscriptions')
        .select('*')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString())
        .maybeSingle();

      if (subscriptionError) {
        throw subscriptionError;
      }

      setHasAccess(
        (!!couponUsage && couponUsage.length > 0) || 
        !!practiceSubscription
      );
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    }
  };

  const loadSongs = async () => {
    try {
      const { data, error } = await supabase
        .from('stripesongs')
        .select('id, creator, song_cover, language, is_free')
        .eq('is_visible', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSongs(data || []);
    } catch (error: any) {
      toast.error('Error loading songs');
    }
  };

  const loadChallenges = async () => {
    try {
      const data = await getActiveChallenges();
      setChallenges(data);
    } catch (error) {
      console.error('Error loading challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkUserCurrentChallenge = async () => {
    if (!user) return;
    
    try {
      const currentChallenge = await getUserChallengeSession(user.id);
      setUserCurrentChallenge(currentChallenge);
    } catch (error) {
      console.error('Error checking user challenge:', error);
    }
  };

  const loadUserParticipatedChallenges = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('stripe_challenge_participants')
        .select('session_id')
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      const participatedSessions = new Set(data?.map(p => p.session_id) || []);
      setUserParticipatedChallenges(participatedSessions);
    } catch (error) {
      console.error('Error loading user participated challenges:', error);
    }
  };

  const isUserAlreadyParticipated = (sessionId: string): boolean => {
    return userParticipatedChallenges.has(sessionId);
  };

  const handleJoinChallenge = (challenge: ChallengeSession) => {
    console.log('Join challenge clicked:', {
      hasAccess,
      songIsFree: challenge.song?.is_free,
      challengeId: challenge.id,
      userId: user?.id
    });
    
    // Check if song is premium and user doesn't have access
    if (!hasAccess && challenge.song && !challenge.song.is_free) {
      console.log('Blocking access to premium challenge');
      toast.error('Premium access required for this challenge');
      return;
    }

    // Check if challenge is expired
    if (new Date(challenge.expires_at) <= new Date()) {
      toast.error('Este desafío ha expirado');
      return;
    }

    // Check if user already participated
    if (isUserAlreadyParticipated(challenge.id)) {
      toast.error('Ya participaste en este desafío');
      return;
    }

    console.log('Proceeding to join challenge');
    // Navigate to practice with challenge mode
    navigate(`/practice/song/${challenge.song_id}`, {
      state: {
        challengeMode: true,
        sessionId: challenge.id,
        expiresAt: challenge.expires_at,
        rewardPoints: challenge.reward_points
      }
    });
  };

  const handleCreateChallenge = async () => {
    if (!selectedSong || !user) return;

    setCreating(true);
    try {
      const session = await createChallengeSession(selectedSong, 10, 720, 100); // 12 hours
      toast.success('¡Desafío creado exitosamente!');
      setShowCreateModal(false);
      setSelectedSong('');
      loadChallenges();
      
      // Auto-join the created challenge
      navigate(`/practice/song/${session.song_id}`, {
        state: {
          challengeMode: true,
          sessionId: session.id,
          expiresAt: session.expires_at,
          rewardPoints: session.reward_points
        }
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const formatTimeLeft = (expiresAt: string) => {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const remaining = Math.max(0, expiry - now);
    
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white relative overflow-hidden">
      {/* Epic Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated Orbs */}
        <motion.div 
          className="absolute -top-20 -left-20 w-40 h-40 bg-gradient-to-br from-yellow-400/20 to-amber-500/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
            x: [0, 50, 0],
            y: [0, -30, 0]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute -bottom-20 -right-20 w-60 h-60 bg-gradient-to-br from-purple-400/15 to-pink-400/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
            x: [0, -40, 0],
            y: [0, 20, 0]
          }}
          transition={{ 
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Floating Particles */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-yellow-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 1, 0.3],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      {/* Header Section */}
      <div className="relative z-10 pt-8 pb-6">
        <div className="w-full px-4 sm:px-6">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-8">
            <Link
              to="/practice"
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-lg rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
            >
              <FaArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back to Practice</span>
            </Link>

            <motion.button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500/80 to-emerald-600/80 border border-green-400/50 hover:border-green-300/70 rounded-full transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-green-500/25"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPlus className="w-4 h-4 text-white" />
              <span className="text-white font-medium">Create Challenge</span>
            </motion.button>
          </div>

          {/* Epic Header */}
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600 rounded-full mb-6 shadow-2xl"
              animate={{ 
                rotate: [0, 5, -5, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <FaTrophy className="w-10 h-10 text-black drop-shadow-lg" />
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-yellow-400/60"
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.6, 0.2, 0.6]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>

            <motion.h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-yellow-200 to-white bg-clip-text text-transparent mb-4"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              🏆 Challenge Mode
            </motion.h1>
            
            <motion.p 
              className="text-lg text-gray-300 max-w-2xl mx-auto px-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Compete with students worldwide in real-time challenges
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* User's Current Challenge */}
      {userCurrentChallenge && (
        <motion.div
          className="relative z-10 w-full px-4 sm:px-6 mb-8"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-gradient-to-r from-blue-500/20 via-purple-500/15 to-indigo-500/20 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/30 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <motion.div
                  className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg"
                  animate={{ 
                    rotate: [0, 360],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <FaRocket className="w-6 h-6 text-white" />
                </motion.div>
                <div>
                  <h3 className="text-white font-bold text-lg">Active Challenge</h3>
                  <p className="text-white/70">In progress</p>
                </div>
              </div>
              <motion.button
                onClick={() => navigate(`/practice/song/${userCurrentChallenge.songId}`, {
                  state: {
                    challengeMode: true,
                    sessionId: userCurrentChallenge.sessionId,
                    expiresAt: userCurrentChallenge.expiresAt,
                    rewardPoints: 100
                  }
                })}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 font-bold shadow-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Continue Battle
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Challenge Content */}
      <div className="relative z-10 w-full px-4 sm:px-6 pb-8">
        <div className="bg-black/60 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl overflow-hidden">
          
          {/* Challenge Header */}
          <div className="bg-gradient-to-r from-yellow-400/20 via-amber-500/15 to-yellow-600/20 p-6 border-b border-gray-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <FaFire className="w-6 h-6 text-orange-400 drop-shadow-lg" />
                </motion.div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Active Battles ({challenges.length})</h2>
                  <p className="text-gray-300">Join epic challenges and compete globally</p>
                </div>
              </div>
            </div>
          </div>

          {/* Challenge Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 mx-auto mb-6"
                >
                  <div className="w-full h-full rounded-full border-4 border-gray-700 border-t-yellow-400 shadow-lg"></div>
                </motion.div>
                <p className="text-gray-300 text-lg font-medium">Loading epic battles...</p>
              </div>
            ) : challenges.length === 0 ? (
              <motion.div 
                className="text-center py-12 bg-white/5 rounded-2xl border border-white/10"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, type: "spring" }}
                >
                  <FaTrophy className="w-20 h-20 text-gray-500 mx-auto mb-6" />
                </motion.div>
                <h4 className="text-2xl font-bold text-white mb-3">Arena Empty</h4>
                <p className="text-white/60 mb-6 text-lg px-4">Be the first to create an epic battle!</p>
                <motion.button
                  onClick={() => setShowCreateModal(true)}
                  className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-black rounded-xl font-bold hover:from-yellow-500 hover:to-amber-600 transition-all duration-300 shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FaPlus className="mr-2 w-4 h-4" />
                  Create First Battle
                </motion.button>
              </motion.div>
            ) : (
              /* Challenge Cards */
              <div className="space-y-6">
                {challenges.map((challenge, index) => (
                  <motion.div
                    key={challenge.id}
                    className="relative overflow-hidden bg-gradient-to-br from-gray-800/80 via-gray-900/60 to-black/80 backdrop-blur-xl rounded-2xl border border-gray-600/50 hover:border-yellow-400/50 transition-all duration-500 shadow-xl group"
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ 
                      duration: 0.5, 
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ 
                      scale: 1.01,
                      transition: { duration: 0.2 }
                    }}
                  >
                    {/* Challenge Content */}
                    <div className="relative p-6">
                      
                      {/* Song Header */}
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="relative">
                          <img
                            src={challenge.song?.song_cover || '/default-song-cover.jpg'}
                            alt={challenge.song?.creator || 'Song'}
                            className="w-16 h-16 rounded-lg object-cover shadow-lg border-2 border-yellow-400/30"
                          />
                          <motion.div
                            className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg"
                            animate={{ 
                              rotate: [0, 360],
                              scale: [1, 1.1, 1]
                            }}
                            transition={{ 
                              duration: 3,
                              repeat: Infinity,
                              ease: "linear"
                            }}
                          >
                            <FaGem className="w-3 h-3 text-black" />
                          </motion.div>
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="text-white font-bold text-lg mb-1">
                            {challenge.song?.creator || 'Mystery Song'}
                          </h4>
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-indigo-200 text-xs rounded-full border border-indigo-400/30 font-medium">
                              {challenge.song?.language || 'spanish'}
                            </span>
                            <motion.div
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                              className="w-2 h-2 bg-green-400 rounded-full shadow-lg"
                            />
                          </div>
                        </div>

                        {/* Elite Badge */}
                        <motion.div
                          animate={{ 
                            scale: [1, 1.05, 1],
                            opacity: [0.8, 1, 0.8]
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          <div className="px-3 py-1 bg-gradient-to-r from-yellow-400/30 to-amber-500/30 text-yellow-200 rounded-full text-xs font-bold border border-yellow-400/50 flex items-center">
                            <FaCrown className="w-3 h-3 mr-1" />
                            ELITE
                          </div>
                        </motion.div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <motion.div 
                          className="text-center bg-gradient-to-br from-yellow-500/20 to-amber-500/20 backdrop-blur-lg rounded-xl p-3 border border-yellow-400/30"
                          whileHover={{ scale: 1.02 }}
                        >
                          <motion.div
                            animate={{ 
                              rotate: [0, 360],
                              scale: [1, 1.05, 1]
                            }}
                            transition={{ 
                              duration: 3,
                              repeat: Infinity,
                              ease: "linear"
                            }}
                          >
                            <FaGem className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                          </motion.div>
                          <div className="text-xl font-bold text-yellow-400">{challenge.reward_points}</div>
                          <div className="text-white/60 text-xs font-medium">Points</div>
                        </motion.div>
                        
                        <motion.div 
                          className="text-center bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-lg rounded-xl p-3 border border-blue-400/30"
                          whileHover={{ scale: 1.02 }}
                        >
                          <motion.div
                            animate={{ 
                              scale: [1, 1.1, 1],
                              rotate: [0, 10, -10, 0]
                            }}
                            transition={{ 
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          >
                            <FaUsers className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                          </motion.div>
                          <div className="text-xl font-bold text-blue-400">{challenge.participant_count || 0}</div>
                          <div className="text-white/60 text-xs font-medium">Warriors</div>
                        </motion.div>
                        
                        <motion.div 
                          className="text-center bg-gradient-to-br from-red-500/20 to-orange-500/20 backdrop-blur-lg rounded-xl p-3 border border-red-400/30"
                          whileHover={{ scale: 1.02 }}
                        >
                          <motion.div
                            animate={{ 
                              scale: [1, 1.05, 1],
                              opacity: [0.8, 1, 0.8]
                            }}
                            transition={{ 
                              duration: 1,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          >
                            <FaClock className="w-5 h-5 text-red-400 mx-auto mb-1" />
                          </motion.div>
                          <div className="text-xl font-bold text-red-400">{formatTimeLeft(challenge.expires_at)}</div>
                          <div className="text-white/60 text-xs font-medium">Time</div>
                        </motion.div>
                      </div>

                      {/* Battle Button */}
                      <motion.button
                        onClick={() => handleJoinChallenge(challenge)}
                        disabled={
                          new Date(challenge.expires_at) <= new Date() || 
                          isUserAlreadyParticipated(challenge.id) ||
                          (!hasAccess && challenge.song && !challenge.song.is_free)
                        }
                        className="relative w-full overflow-hidden bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-black rounded-xl font-bold py-4 hover:from-yellow-500 hover:via-amber-600 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-yellow-400/25 group"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        {/* Button Background Animation */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                          animate={{ x: ['-100%', '100%'] }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            ease: "linear"
                          }}
                        />
                        
                        {/* Button Content */}
                        <span className="relative flex items-center justify-center">
                          {new Date(challenge.expires_at) <= new Date() ? (
                            <>
                              <FaClock className="mr-2 w-4 h-4" />
                              Battle Ended
                            </>
                          ) : isUserAlreadyParticipated(challenge.id) ? (
                            <>
                              <FaShieldAlt className="mr-2 w-4 h-4" />
                              Already Participated
                            </>
                          ) : !hasAccess && challenge.song && !challenge.song.is_free ? (
                            <>
                              <FaLock className="mr-2 w-4 h-4" />
                              Premium Required
                            </>
                          ) : (
                            <>
                              <motion.div
                                animate={{ 
                                  scale: [1, 1.1, 1],
                                  rotate: [0, 10, -10, 0]
                                }}
                                transition={{ 
                                  duration: 1,
                                  repeat: Infinity,
                                  ease: "easeInOut"
                                }}
                              >
                                <FaBolt className="mr-2 w-4 h-4" />
                              </motion.div>
                              Join the Battle!
                            </>
                          )}
                        </span>
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <motion.div 
          className="relative z-10 w-full px-4 sm:px-6 mt-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <div className="bg-gradient-to-r from-gray-900/80 to-black/60 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
            <div className="text-center">
              <motion.div
                className="flex items-center justify-center space-x-3 mb-4"
                animate={{ 
                  scale: [1, 1.02, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <FaTrophy className="w-6 h-6 text-yellow-400" />
                <span className="text-gray-300 font-medium text-lg">
                  Compete and dominate the global leaderboard
                </span>
                <FaBolt className="w-6 h-6 text-yellow-400" />
              </motion.div>
              
              {/* Animated Dots */}
              <div className="flex justify-center space-x-2">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-2 h-2 bg-yellow-400/60 rounded-full"
                    animate={{ 
                      scale: [1, 1.3, 1],
                      opacity: [0.4, 1, 0.4]
                    }}
                    transition={{ 
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.2,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Create Challenge Modal */}
      {showCreateModal && (
        <motion.div
          className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-2xl shadow-2xl w-full max-w-md border border-gray-600/50 overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Modal Header */}
            <div className="relative p-6 border-b border-gray-700/50 bg-gradient-to-r from-yellow-400/10 to-amber-500/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <motion.div
                    className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg"
                    animate={{ 
                      rotate: [0, 5, -5, 0],
                      scale: [1, 1.05, 1]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <FaTrophy className="w-6 h-6 text-black" />
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Create Battle</h3>
                    <p className="text-gray-300 text-sm">Challenge students worldwide</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="relative p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-white mb-3">
                  Select Battle Song
                </label>
                <select
                  value={selectedSong}
                  onChange={(e) => setSelectedSong(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800/80 backdrop-blur-lg border border-gray-600/50 rounded-xl text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-300"
                >
                  <option value="">Choose your musical weapon...</option>
                  {songs.map((song) => (
                    <option key={song.id} value={song.id}>
                      {song.creator} ({song.language})
                    </option>
                  ))}
                </select>
              </div>

              {/* Battle Configuration */}
              <div className="bg-gradient-to-br from-yellow-400/10 via-amber-500/5 to-yellow-600/10 border border-yellow-400/30 rounded-xl p-6 backdrop-blur-lg">
                <div className="flex items-center space-x-3 mb-4">
                  <motion.div
                    animate={{ 
                      rotate: [0, 360],
                      scale: [1, 1.05, 1]
                    }}
                    transition={{ 
                      duration: 4,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  >
                    <FaAtom className="w-6 h-6 text-yellow-400" />
                  </motion.div>
                  <h4 className="font-bold text-yellow-200 text-lg">Configuration</h4>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-xl font-bold text-white mb-1">12</div>
                    <div className="text-white/60 text-xs font-medium">Hours</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-xl font-bold text-white mb-1">10</div>
                    <div className="text-white/60 text-xs font-medium">Max Players</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-xl font-bold text-white mb-1">100</div>
                    <div className="text-white/60 text-xs font-medium">Points</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col space-y-3 pt-2">
                <motion.button
                  onClick={handleCreateChallenge}
                  disabled={!selectedSong || creating}
                  className="relative w-full overflow-hidden bg-gradient-to-r from-yellow-400 to-amber-500 text-black rounded-xl font-bold py-4 hover:from-yellow-500 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-yellow-400/25 group"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  {/* Button Background Animation */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                  
                  {/* Button Content */}
                  <span className="relative flex items-center justify-center">
                    {creating ? (
                      <>
                        <FaSpinner className="animate-spin mr-2 w-4 h-4" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <motion.div
                          animate={{ 
                            rotate: [0, 360],
                            scale: [1, 1.05, 1]
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          <FaRocket className="mr-2 w-4 h-4" />
                        </motion.div>
                        Start Battle!
                      </>
                    )}
                  </span>
                </motion.button>
                
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="w-full px-6 py-3 bg-gray-700/80 hover:bg-gray-600/80 rounded-xl text-white font-medium transition-all duration-300 backdrop-blur-lg border border-gray-600/50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}